import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Globe2, Mic, ShieldCheck, Moon, Sun } from "lucide-react";
import { AuroraLogo, AuroraWordmark } from "@/components/AuroraLogo";
import { AuthPanel } from "@/components/AuthPanel";
import { useAuth, useTheme } from "@/context/AuthContext";
import { EncryptedBadge } from "@/components/EncryptedBadge";
import { Footer } from "@/components/Footer";
import { InstallAppButton } from "@/components/InstallAppButton";
import { DigitalRain } from "@/components/DigitalRain";

const DEMO = [
  { from: "IT", text: "Ciao a tutti, iniziamo la riunione.", to: "Hi everyone, let's start the meeting." },
  { from: "DE", text: "Ich habe die Zahlen vorbereitet.", to: "I have prepared the numbers." },
  { from: "JA", text: "素晴らしいですね、続けましょう。", to: "That's great, let's continue." },
];

const FEATURES = [
  { icon: Mic, k: "01", t: "Speak any language", d: "Push-to-talk voice, dictation or text. Aurora transcribes instantly." },
  { icon: Globe2, k: "02", t: "Everyone in their own tongue", d: "Up to 4 people and 4 languages: each one reads and hears the others in their own language." },
  { icon: ShieldCheck, k: "03", t: "Rooms that vanish", d: "When the call ends, the room closes. Next room, new rules." },
];

const BARS = Array.from({ length: 42 }, (_, k) => k);
const FEED_IN = { opacity: 0, y: 8 };
const FEED_SHOW = { opacity: 1, y: 0 };
const FEED_T = { duration: 0.35 };

const DemoFeed = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % DEMO.length), 3200);
    return () => clearInterval(t);
  }, []);
  const d = DEMO[i];
  return (
    <motion.div key={i} initial={FEED_IN} animate={FEED_SHOW} transition={FEED_T}
      className="holo-panel corners max-w-xl p-5" data-testid="landing-demo-feed">
      <div className="mb-3 flex items-center justify-between font-mono text-[11px] tracking-widest text-muted-foreground">
        <span>[{d.from}] → [EN] · SPEECHFLOW</span>
        <span className="text-[color:var(--au-green)]">● 0.9s</span>
      </div>
      <p className="text-sm text-muted-foreground">{d.text}</p>
      <p className="mt-2 font-display text-lg font-bold">{d.to}</p>
      <div className="mt-4 flex h-6 items-end gap-[3px]">
        {BARS.map((k) => (
          <span key={`bar-${k}`} className="w-[3px] bg-cyan-400/70" style={{ height: `${20 + Math.abs(Math.sin(k * 0.7 + i)) * 80}%` }} />
        ))}
      </div>
    </motion.div>
  );
};

export default function Landing() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [theme, toggle] = useTheme();
  const next = params.get("next");
  if (user) return <Navigate to={user.role === "admin" ? "/admin" : next || "/dashboard"} replace />;

  return (
    <div className="dark relative min-h-screen text-foreground">
      <DigitalRain intense />
      <div className="relative z-10">
      <header className="mx-auto flex h-16 max-w-[1440px] items-center justify-between px-4 sm:px-8">
        <div className="flex items-center gap-3">
          <AuroraLogo size={34} />
          <span className="leading-tight" data-testid="landing-brand"><span className="block font-display text-sm font-extrabold">SPEECH<span className="text-[color:var(--au-green)]">FLOW</span></span><span className="block font-mono text-[9px] tracking-widest text-[color:var(--au-violet)]">POWERED BY AURORA-ON</span></span>
        </div>
        <div className="flex items-center gap-3 sm:gap-4">
          <EncryptedBadge testId="landing-encrypted-badge" />
          <InstallAppButton testId="landing-install-app-button" />
          <span className="hidden font-mono text-[11px] tracking-widest text-muted-foreground lg:inline">SYS.STATUS <span className="text-[color:var(--au-green)]">NOMINAL</span></span>
          <button onClick={toggle} className="icon-btn" data-testid="landing-theme-toggle" aria-label="Toggle theme">
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </div>
      </header>

      <main className="mx-auto grid max-w-[1440px] grid-cols-1 gap-12 px-4 pb-24 pt-10 sm:px-8 lg:grid-cols-12 lg:gap-16 lg:pt-20">
        <section className="lg:col-span-7">
          <div className="fade-up flex items-center gap-10">
            <AuroraLogo size={88} rings testId="landing-hero-logo" />
            <div className="eyebrow">Real-time AI interpreter</div>
          </div>
          <h1 className="fade-up mt-12 font-display text-4xl font-extrabold leading-[0.95] tracking-tighter sm:text-5xl lg:text-6xl" style={{ animationDelay: "80ms" }}>
            Every voice.<br />One language.<br /><AuroraWordmark />
          </h1>
          <p className="fade-up mt-8 max-w-lg text-sm leading-relaxed text-muted-foreground sm:text-base" style={{ animationDelay: "160ms" }}>
            Open a room, invite anyone, speak your own language. Aurora transcribes, translates and speaks every message
            aloud in each listener&apos;s own language — with a natural male or female neural voice.
          </p>
          <div className="fade-up mt-10" style={{ animationDelay: "240ms" }}><DemoFeed /></div>
          <div className="mt-16 grid grid-cols-1 border-t border-border sm:grid-cols-3">
            {FEATURES.map((f, idx) => (
              <div key={f.k} className="fade-up border-b border-border py-6 sm:border-b-0 sm:pr-6" style={{ animationDelay: `${320 + idx * 80}ms` }}>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-[color:var(--au-green)]">[{f.k}]</span>
                  <f.icon size={16} className="text-[color:var(--au-cyan)]" />
                </div>
                <h3 className="mt-4 font-display text-base font-bold">{f.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{f.d}</p>
              </div>
            ))}
          </div>
        </section>
        <section className="order-first lg:order-none lg:col-span-5 lg:pt-6">
          {next?.startsWith("/room/") && (
            <div className="holo-panel mb-6 border-l-2 border-[color:var(--au-green)] p-4" data-testid="landing-invite-notice">
              <div className="eyebrow">Invitation received</div>
              <p className="mt-2 text-sm">You&apos;ve been invited to <b>SpeechFlow {next.split("/room/")[1]}</b>. Sign in or create an account to join.</p>
            </div>
          )}
          <AuthPanel next={next} />
        </section>
      </main>
      <Footer />
      </div>
    </div>
  );
}
